import CommentIcon from '@atlaskit/icon/core/comment';
import CommentAddIcon from '@atlaskit/icon/core/comment-add';
import GrowDiagonalIcon from '@atlaskit/icon/core/grow-diagonal';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { messages } from '@databases/intl/messages';
import React, {
  type FunctionComponent,
  type FocusEvent,
  type ReactElement,
  useMemo,
  useState
} from 'react';
import { useIntl } from 'react-intl-next';
import { getExperimentValue } from '@canvas/client-feature-flags/experiments';
import CellCommentsButton from './CellCommentsButton.tsx';

const STANDARD_ICON_SIZE = '24px';

const FloatingOverlayStyles = xcss({
  position: 'absolute',
  top: 'space.050',
  right: 'space.050',
  opacity: 0,
  transition: 'opacity 0.2s ease-in-out',
  pointerEvents: 'none',
  boxShadow: 'elevation.shadow.overlay',
  backgroundColor: 'elevation.surface.overlay',
  borderRadius: 'radius.small',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center'
});

const GroupedOverlayStyles = xcss({
  boxShadow: 'elevation.shadow.raised',
  borderRadius: 'radius.medium',
  top: 'space.100',
  right: 'space.100'
});

const PaddedGroupedOverlayStyles = xcss({
  padding: 'space.025'
});

const GroupedButtonStyles = xcss({
  minWidth: STANDARD_ICON_SIZE,
  width: 'auto',
  height: STANDARD_ICON_SIZE,
  borderRadius: 'radius.medium',
  ':hover': {
    backgroundColor: 'color.background.neutral.subtle.hovered'
  }
});

const GroupedButtonsContainerStyles = xcss({
  gap: 'space.025'
});

const CollapsibleActionsStyles = xcss({
  overflow: 'hidden',
  flexShrink: 0,
  height: '22px',
  transition:
    'width 150ms cubic-bezier(0.2, 0, 0, 1), opacity 150ms ease-out, transform 150ms ease-out',
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none'
  }
});

const CollapsibleActionsContentStyles = xcss({
  display: 'flex',
  alignItems: 'center',
  width: 'max-content',
  height: '22px',
  gap: 'space.025',
  paddingRight: 'space.025'
});

const FloatingOverlayVisibleStyles = xcss({
  opacity: 1,
  pointerEvents: 'auto'
});

const ButtonsContainerStyles = xcss({
  display: 'flex',
  alignItems: 'center'
});

const BaseButtonStyles = xcss({
  width: STANDARD_ICON_SIZE,
  height: STANDARD_ICON_SIZE,
  padding: 'space.0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background-color 0.2s',
  ':hover': {
    cursor: 'pointer',
    backgroundColor: 'color.background.neutral.hovered'
  }
});

const CommentButtonStyles = xcss({
  minWidth: STANDARD_ICON_SIZE,
  width: 'auto',
  borderRadius: 'radius.small',
  paddingInline: 'space.075'
});

const AddThreadButtonStyles = xcss({
  width: STANDARD_ICON_SIZE,
  height: STANDARD_ICON_SIZE,
  borderRadius: 'radius.small',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background-color 0.2s',
  ':hover': {
    cursor: 'pointer',
    backgroundColor: 'color.background.neutral.hovered'
  }
});

const ViewThreadButtonSelectedStyles = xcss({
  ':hover': {
    backgroundColor: 'color.background.accent.blue.subtlest'
  }
});

export interface FloatingCellActionButtonsProps {
  onCommentClick: (e: React.MouseEvent) => void;
  commentToggled: boolean;
  hasThreads: boolean;
  commentCount: number;
  shouldShowHoverButton: boolean;
  valueId: string;
  isHovered: boolean;
  isFirstField: boolean;
  handleExpandEntry: (e: React.MouseEvent) => void;
  panel: boolean;
  cellAction?: ReactElement;
  appearance?: 'default' | 'grouped';
  shouldShowCommentButton: boolean;
}

const FloatingCellActionButtons: FunctionComponent<FloatingCellActionButtonsProps> = ({
  onCommentClick,
  commentToggled,
  hasThreads,
  commentCount,
  shouldShowHoverButton,
  valueId,
  isHovered,
  isFirstField,
  handleExpandEntry,
  panel,
  cellAction,
  appearance = 'default',
  shouldShowCommentButton
}) => {
  const { formatMessage } = useIntl();
  const [isActionGroupHovered, setIsActionGroupHovered] = useState(false);
  const [isActionGroupFocused, setIsActionGroupFocused] = useState(false);

  const commentString = useMemo(() => {
    return commentCount > 9 ? '9+' : commentCount.toString();
  }, [commentCount]);

  const containerStyles = useMemo(() => {
    return isHovered
      ? [FloatingOverlayStyles, FloatingOverlayVisibleStyles]
      : [FloatingOverlayStyles];
  }, [isHovered]);

  const shouldRenderExpandButton = isFirstField && !panel;
  const shouldRemoveAddCommentButton =
    shouldShowCommentButton &&
    !hasThreads &&
    shouldShowHoverButton &&
    getExperimentValue('databasesCellHoverNoiseReduction', 'isEnabled', {
      fireExperimentExposure: true
    });
  const shouldRenderCommentButton =
    shouldShowCommentButton &&
    (hasThreads || (shouldShowHoverButton && !shouldRemoveAddCommentButton));

  const useFloatingAppearance = getExperimentValue(
    'databasesFrontendAiFieldsSummary',
    'enabled',
    {
      fireExperimentExposure: false
    }
  );
  const usePersistentGroup =
    shouldShowCommentButton && hasThreads && useFloatingAppearance;
  const useGroupedStyles = appearance === 'grouped' || useFloatingAppearance;
  const areActionsExpanded = isActionGroupHovered || isActionGroupFocused;
  const additionalActionsWidth =
    (Number(shouldRenderExpandButton) + Number(Boolean(cellAction))) * 26;

  const AddCommentActionIcon = appearance === 'grouped' ? CommentIcon : CommentAddIcon;

  const renderCommentButton = () => {
    if (!shouldRenderCommentButton) {
      return null;
    }

    if (hasThreads) {
      if (useGroupedStyles) {
        return (
          <CellCommentsButton
            isInActionGroup
            onClick={onCommentClick}
            commentToggled={commentToggled}
            hasThreads={hasThreads}
            commentCount={commentCount}
            shouldShowCommentButton={shouldShowCommentButton}
            shouldShowHoverButton={shouldShowHoverButton}
            valueId={valueId}
          />
        );
      }
      return (
        <Tooltip
          position="top"
          content={formatMessage(
            messages['comments.show-comment-thread-button-tooltip'],
            {
              isMulti: commentCount
            }
          )}
        >
          <Box
            onClick={onCommentClick}
            xcss={[
              BaseButtonStyles,
              CommentButtonStyles,
              useGroupedStyles && xcss({ paddingInline: 'space.050' }),
              useGroupedStyles && GroupedButtonStyles,
              commentToggled && !useGroupedStyles && ViewThreadButtonSelectedStyles
            ]}
            id={`cell-view-thread-button-${valueId}`}
            testId="cell-view-thread-button"
          >
            <Inline
              space={useGroupedStyles ? 'space.075' : 'space.050'}
              alignBlock="center"
              xcss={xcss({
                color: commentToggled ? 'color.text.selected' : 'color.text'
              })}
            >
              <CommentIcon
                label={formatMessage(
                  messages['comments.view-comment-thread-button-label']
                )}
                size="small"
                color={
                  useGroupedStyles
                    ? token('color.icon.subtle')
                    : commentToggled
                      ? token('color.icon.accent.blue')
                      : token('color.icon')
                }
              />
              <Box
                xcss={xcss({ font: 'font.body.small', whiteSpace: 'nowrap' })}
                style={
                  useGroupedStyles ? { color: token('color.icon.subtle') } : undefined
                }
              >
                {commentString}
              </Box>
            </Inline>
          </Box>
        </Tooltip>
      );
    }

    if (shouldShowHoverButton) {
      return (
        <Tooltip
          position="top"
          content={formatMessage(messages['comments.add-comment-thread-button-label'])}
        >
          <Box
            xcss={[AddThreadButtonStyles, useGroupedStyles && GroupedButtonStyles]}
            onClick={onCommentClick}
            id={`cell-add-thread-button-${valueId}`}
            testId="cell-add-thread-button"
          >
            <AddCommentActionIcon
              label={formatMessage(messages['comments.add-comment-thread-button-label'])}
              size={'small'}
              color={token('color.icon')}
            />
          </Box>
        </Tooltip>
      );
    }

    return null;
  };

  const renderExpandButton = () => (
    <Tooltip position="top" content={formatMessage(messages['entry-actions.expand'])}>
      <Box
        xcss={[BaseButtonStyles, useGroupedStyles && GroupedButtonStyles]}
        onClick={handleExpandEntry}
        id={`cell-expand-entry-button-${valueId}`}
      >
        <GrowDiagonalIcon
          label={formatMessage(messages['entry-actions.expand'])}
          size={'small'}
          color={token('color.icon')}
        />
      </Box>
    </Tooltip>
  );

  if (!shouldRenderExpandButton && !shouldRenderCommentButton && !cellAction) {
    return null;
  }

  return (
    <Box
      xcss={[
        ...containerStyles,
        useGroupedStyles && GroupedOverlayStyles,
        useGroupedStyles && !shouldRenderCommentButton && PaddedGroupedOverlayStyles,
        useGroupedStyles &&
          hasThreads &&
          shouldShowCommentButton &&
          FloatingOverlayVisibleStyles
      ]}
      testId="floating-cell-actions"
      onMouseEnter={usePersistentGroup ? () => setIsActionGroupHovered(true) : undefined}
      onMouseLeave={usePersistentGroup ? () => setIsActionGroupHovered(false) : undefined}
      onFocusCapture={
        usePersistentGroup ? () => setIsActionGroupFocused(true) : undefined
      }
      onBlurCapture={
        usePersistentGroup
          ? (event: FocusEvent<HTMLDivElement>) => {
              if (!event.currentTarget.contains(event.relatedTarget)) {
                setIsActionGroupFocused(false);
              }
            }
          : undefined
      }
    >
      <Box
        xcss={[
          ButtonsContainerStyles,
          useGroupedStyles && !usePersistentGroup && GroupedButtonsContainerStyles
        ]}
      >
        {usePersistentGroup ? (
          <Box
            xcss={CollapsibleActionsStyles}
            testId="collapsible-cell-actions"
            aria-hidden={!areActionsExpanded}
            ref={(element: HTMLDivElement | null) => {
              if (element) {
                element.inert = !areActionsExpanded;
              }
            }}
            style={{
              width: areActionsExpanded ? additionalActionsWidth : 0,
              opacity: areActionsExpanded ? 1 : 0,
              transform: areActionsExpanded ? 'translateX(0)' : 'translateX(8px)',
              pointerEvents: areActionsExpanded ? 'auto' : 'none'
            }}
          >
            <Box xcss={CollapsibleActionsContentStyles}>
              {shouldRenderExpandButton && renderExpandButton()}
              {cellAction}
            </Box>
          </Box>
        ) : (
          <>
            {shouldRenderExpandButton &&
              (!useGroupedStyles || isHovered) &&
              renderExpandButton()}
            {appearance === 'grouped' && cellAction}
          </>
        )}
        {renderCommentButton()}
        {!usePersistentGroup && appearance !== 'grouped' && cellAction}
      </Box>
    </Box>
  );
};

export default FloatingCellActionButtons;
